import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import Container from '../../Components/Container'
import DataTable from 'react-data-table-component'
import toast, { Toaster } from 'react-hot-toast'
import { FaBoxes, FaExchangeAlt, FaBarcode, FaTimes } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import debounce from 'lodash/debounce'

const readJson = (res) => {
    if (!res.ok) {
        throw new Error('Sunucu yanıt vermedi')
    }
    return res.json()
}

const isListError = (data) =>
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    data.status === 400

const isTransferFail = (data) => {
    if (data == null || typeof data !== 'object') return true
    const st = data.status
    if (typeof st === 'number') return st !== 200
    return true
}

const StoreDetail = () => {
    const [open, setOpen] = useState(false)
    const [stores, setStores] = useState([])
    const [products, setProducts] = useState([])
    const [barcode, setBarcode] = useState('')
    const [selectedStore, setSelectedStore] = useState('')
    const [selectedRows, setSelectedRows] = useState([])
    /** false = tekil transfer (adet alanı açık), true = toplu (multi) */
    const [isBulkModal, setIsBulkModal] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [loading, setLoading] = useState(true)
    const [transferLoading, setTransferLoading] = useState(false)
    const [count, setCount] = useState(1)
    const [transferProductId, setTransferProductId] = useState(null)
    const storeSelectRef = useRef(null)

    const fetchProducts = useCallback(async () => {
        setLoading(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'select')
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'product.php',
                {
                    method: 'POST',
                    body: formdata,
                }
            ).then(readJson)

            if (isListError(data)) {
                toast.error(data.message || 'Liste alınamadı')
                setProducts([])
                return
            }
            setProducts(Array.isArray(data) ? data : [])
        } catch {
            toast.error('Ürünler yüklenirken bir hata oluştu')
            setProducts([])
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchStores = useCallback(async () => {
        try {
            const formdata = new FormData()
            formdata.append('action', 'select')
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'store.php',
                {
                    method: 'POST',
                    body: formdata,
                }
            ).then(readJson)

            if (isListError(data)) {
                toast.error(data.message || 'Depolar alınamadı')
                setStores([])
                return
            }
            const list = Array.isArray(data) ? data : []
            setStores(list)
            if (list.length > 0) {
                setSelectedStore((prev) => {
                    const ok = list.some((s) => s.name === prev)
                    return ok ? prev : list[0].name
                })
            }
        } catch {
            toast.error('Depolar yüklenirken bir hata oluştu')
            setStores([])
        }
    }, [])

    const runBarcodeSearch = useMemo(
        () =>
            debounce((query) => {
                const formdata = new FormData()
                formdata.append('action', 'findByBarcodeForStore')
                formdata.append('barcode', query)

                fetch(process.env.REACT_APP_BASE_URL + 'product.php', {
                    method: 'POST',
                    body: formdata,
                })
                    .then(readJson)
                    .then((data) => {
                        if (data != null && data.status === 200) {
                            const rows = Array.isArray(data.data)
                                ? data.data
                                : []
                            setProducts(rows)
                            return
                        }
                        toast.error(
                            (data && data.message) || 'Arama başarısız oldu'
                        )
                        setProducts([])
                    })
                    .catch(() => {
                        toast.error('Arama sırasında bir hata oluştu')
                        setProducts([])
                    })
                    .finally(() => setLoading(false))
            }, 500),
        []
    )

    useEffect(() => {
        fetchStores()
    }, [fetchStores])

    useEffect(() => {
        const q = searchQuery.trim()
        if (!q) {
            runBarcodeSearch.cancel()
            fetchProducts()
            return
        }
        setLoading(true)
        runBarcodeSearch(q)
        return () => runBarcodeSearch.cancel()
    }, [searchQuery, fetchProducts, runBarcodeSearch])

    const resetModalState = useCallback(() => {
        setOpen(false)
        setIsBulkModal(false)
        setCount(1)
        setTransferProductId(null)
        setBarcode('')
    }, [])

    const closeModal = useCallback(() => {
        if (transferLoading) return
        resetModalState()
    }, [transferLoading, resetModalState])

    const openSingleTransfer = useCallback((row) => {
        setTransferProductId(row.id)
        setBarcode(row.barcode ?? '')
        setIsBulkModal(false)
        setCount(1)
        setOpen(true)
    }, [])

    const openBulkTransfer = useCallback(() => {
        setIsBulkModal(true)
        setOpen(true)
    }, [])

    useEffect(() => {
        if (!open) return
        const raf = requestAnimationFrame(() =>
            storeSelectRef.current?.focus()
        )
        const onKey = (e) => {
            if (e.key === 'Escape' && !transferLoading) closeModal()
        }
        document.addEventListener('keydown', onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [open, transferLoading, closeModal])

    const handleTransfer = async () => {
        if (!selectedStore) {
            toast.error('Lütfen bir depo seçin')
            return
        }

        const single = !isBulkModal
        if (single) {
            const n = Number(count)
            if (!Number.isFinite(n) || n < 1) {
                toast.error('Geçerli bir adet girin (en az 1)')
                return
            }
            if (transferProductId == null || !String(barcode).trim()) {
                toast.error('Ürün bilgisi eksik')
                return
            }
        } else {
            if (selectedRows.length === 0) {
                toast.error('En az bir ürün seçin')
                return
            }
        }

        setTransferLoading(true)
        try {
            const formdata = new FormData()
            if (single) {
                formdata.append('action', 'storeUpdate')
                formdata.append('id', transferProductId)
                formdata.append('barcode', String(barcode).trim())
                formdata.append('store', selectedStore)
                formdata.append('count', String(count))
            } else {
                formdata.append('action', 'multi')
                formdata.append('store', selectedStore)
                formdata.append('selected', JSON.stringify(selectedRows))
            }

            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'product.php',
                {
                    method: 'POST',
                    body: formdata,
                }
            ).then(readJson)

            if (isTransferFail(data)) {
                throw new Error(data.message || 'İşlem reddedildi')
            }
            toast.success(data.message || 'İşlem tamamlandı')
            resetModalState()
            setSelectedRows([])
            fetchProducts()
        } catch (error) {
            toast.error(error.message || 'İşlem sırasında bir hata oluştu')
        } finally {
            setTransferLoading(false)
        }
    }

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60'

    const columns = useMemo(
        () => [
            {
                name: 'No',
                selector: (row) => row.id,
                sortable: true,
                width: '80px',
            },
            {
                name: 'Barkod',
                selector: (row) => row.barcode,
                sortable: true,
                width: '130px',
            },
            {
                name: 'Ürün Adı',
                selector: (row) => row.name,
                sortable: true,
                grow: 2,
            },
            {
                name: 'Depo',
                selector: (row) => row.store,
                sortable: true,
            },
            {
                name: 'Adet',
                selector: (row) => row.count,
                sortable: true,
                width: '100px',
            },
            {
                name: 'Maliyet',
                selector: (row) => `${row.cost ?? '—'}₺`,
                sortable: true,
                width: '120px',
            },
            {
                name: 'Fiyat',
                selector: (row) => `${row.price ?? '—'}₺`,
                sortable: true,
                width: '120px',
            },
            {
                name: 'İşlem',
                cell: (row) => (
                    <button
                        type='button'
                        onClick={() => openSingleTransfer(row)}
                        className='inline-flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50'
                        disabled={transferLoading}
                    >
                        <FaExchangeAlt size={14} />
                        Aktar
                    </button>
                ),
                width: '130px',
            },
        ],
        [transferLoading, openSingleTransfer]
    )

    const customStyles = useMemo(
        () => ({
            table: {
                style: {
                    backgroundColor: 'white',
                },
            },
            headRow: {
                style: {
                    backgroundColor: '#f8fafc',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e2e8f0',
                    minHeight: '52px',
                },
            },
            headCells: {
                style: {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    paddingTop: '0.875rem',
                    paddingBottom: '0.875rem',
                },
            },
            cells: {
                style: {
                    paddingTop: '0.875rem',
                    paddingBottom: '0.875rem',
                    fontSize: '0.875rem',
                },
            },
        }),
        []
    )

    const emptySearch = !searchQuery.trim()

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{
                    className: 'text-sm font-medium shadow-lg',
                }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='transfer-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !transferLoading && closeModal()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='transfer-modal-title'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <div className='flex items-start gap-3'>
                                        <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600'>
                                            <FaExchangeAlt className='text-lg' />
                                        </div>
                                        <div>
                                            <h2
                                                id='transfer-modal-title'
                                                className='text-lg font-bold tracking-tight text-slate-900'
                                            >
                                                Ürün transferi
                                            </h2>
                                            <p className='mt-1 text-sm text-slate-600'>
                                                {isBulkModal
                                                    ? `${selectedRows.length} ürün seçildi — hedef depoyu seçin.`
                                                    : 'Ürünü seçili depoya taşıyın.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !transferLoading && closeModal()
                                        }
                                        disabled={transferLoading}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FaTimes size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label
                                        htmlFor='transfer-target-store'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Hedef depo
                                    </label>
                                    <select
                                        ref={storeSelectRef}
                                        id='transfer-target-store'
                                        value={selectedStore}
                                        onChange={(e) =>
                                            setSelectedStore(e.target.value)
                                        }
                                        className={inputClass}
                                        disabled={
                                            transferLoading ||
                                            stores.length === 0
                                        }
                                    >
                                        {stores.length === 0 ? (
                                            <option value=''>
                                                Depo bulunamadı
                                            </option>
                                        ) : (
                                            stores.map((store) => (
                                                <option
                                                    key={store.id}
                                                    value={store.name}
                                                >
                                                    {store.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>

                                {!isBulkModal && (
                                    <div>
                                        <label
                                            htmlFor='transfer-count'
                                            className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                        >
                                            Adet
                                        </label>
                                        <input
                                            id='transfer-count'
                                            type='number'
                                            min={1}
                                            value={count}
                                            onChange={(e) =>
                                                setCount(e.target.value)
                                            }
                                            className={inputClass}
                                            placeholder='Transfer edilecek adet'
                                            disabled={transferLoading}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={closeModal}
                                    disabled={transferLoading}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={handleTransfer}
                                    disabled={transferLoading}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {transferLoading ? (
                                        <div className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : (
                                        <>
                                            <FaExchangeAlt size={14} />
                                            Transfer et
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/50 sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex items-start gap-4'>
                            <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                                <FaBoxes className='text-xl' />
                            </div>
                            <div>
                                <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
                                    Ürün transferi
                                </h1>
                                <p className='mt-1 max-w-xl text-sm leading-relaxed text-slate-600'>
                                    Barkod ile arayın veya satır seçerek
                                    depolar arası toplu transfer yapın.
                                </p>
                                <p className='mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                                    {products.length} ürün
                                    {!emptySearch ? ' (arama)' : ''}
                                </p>
                            </div>
                        </div>
                        <div className='flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center'>
                            <div className='relative min-w-0 sm:min-w-[18rem]'>
                                <FaBarcode className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                                <input
                                    type='text'
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    placeholder='Barkod ile ara…'
                                    className={`${inputClass} pl-10`}
                                />
                            </div>
                            {selectedRows.length > 0 && (
                                <button
                                    type='button'
                                    onClick={openBulkTransfer}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50'
                                    disabled={transferLoading}
                                >
                                    <FaExchangeAlt size={14} />
                                    Toplu transfer ({selectedRows.length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className='overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm'>
                    <DataTable
                        columns={columns}
                        data={products}
                        selectableRows
                        pagination
                        progressPending={loading}
                        progressComponent={
                            <div className='flex flex-col items-center justify-center gap-3 py-20'>
                                <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                                <p className='text-sm font-medium text-slate-600'>
                                    Yükleniyor…
                                </p>
                            </div>
                        }
                        onSelectedRowsChange={({ selectedRows: rows }) =>
                            setSelectedRows(rows ?? [])
                        }
                        customStyles={customStyles}
                        noDataComponent={
                            <div className='flex flex-col items-center justify-center px-6 py-16 text-center'>
                                <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400'>
                                    <FaBoxes className='text-2xl' />
                                </div>
                                <p className='text-base font-semibold text-slate-800'>
                                    Ürün bulunamadı
                                </p>
                                <p className='mt-2 max-w-sm text-sm text-slate-600'>
                                    {emptySearch
                                        ? 'Henüz kayıt yok veya liste yüklenemedi.'
                                        : 'Bu barkoda ait ürün yok; farklı bir barkod deneyin veya aramayı temizleyin.'}
                                </p>
                            </div>
                        }
                    />
                </div>
            </div>
        </Container>
    )
}

export default StoreDetail
